'use client'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'
import { useInfiniteBlogPosts } from '../hooks/use-blog-posts'
import { BlogItem } from './BlogItem'

export const NewlyUpdatedBlogs = ({
  categoryId = 6,
  pageSize = 4,
}: {
  children?: React.ReactNode
  categoryId?: number
  pageSize?: number
}) => {
  const t = useTranslations()

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    listBlogs: currentItems,
  } = useInfiniteBlogPosts({
    categoryId,
    pageSize,
    action: 'get_post_list',
  })

  const observerTarget = useRef<HTMLDivElement>(null)

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    const currentTarget = observerTarget.current
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget)
      }
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const ItemsBlog = () => {
    if (isLoading) return <LoadingBlog />
    return (
      <>
        {currentItems.map((blog, index) => {
          if (!blog) return null
          if (index == 0) {
            return (
              <BlogItem
                key={blog?.url_key}
                blog={blog}
                sx={{
                  p: 2,
                  gap: 2,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#F4F5F8',
                  alignItems: 'center',
                  flexDirection: 'column',
                  '&:hover': {
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  },
                  width: '100%',
                }}
                blogContentProps={{ sx: { flex: 1, minWidth: 0, width: '100%' } }}
                blogImageProps={{
                  sx: {
                    flex: 1,
                    minWidth: 0,
                    borderRadius: '4px',
                    aspectRatio: '756.00/425.25',
                    objectFit: 'cover',
                  },
                }}

              />
            )
          }
          return (
            <BlogItem
              key={blog.url_key}
              blog={blog}
              sx={{
                p: 2,
                gap: 2,
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#F4F5F8'),
                alignItems: 'center',
                '&:hover': {
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                },
                flexDirection: { sm: 'row', xs: 'column' },
                width: '100%',
              }}
              blogContentProps={{ sx: { flex: 1, minWidth: 0, width: "100%" } }}
              blogImageProps={{
                sx: { width: { xs: '100%', sm: '15rem' }, minWidth: 0, borderRadius: '4px' },
              }}
            />
          )
        })}

        {isFetchingNextPage && <LoadingBlog length={2} />}
        <div ref={observerTarget} style={{ height: '20px' }} />
      </>
    )
  }

  return (
    <Stack direction={'column'} flexWrap="wrap" gap={2} alignItems="flex-start">
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: '20px', sm: '26px', md: '28px', lg: '32px' },
          textTransform: 'uppercase',
          fontWeight: 800,
          lineHeight: 1.3,
        }}
      >
        {t('MỚI CẬP NHẬT')}
      </Typography>
      <ItemsBlog />
    </Stack>
  )
}
const LoadingBlog = ({ length = 4 }: { length?: number }) => {
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {Array.from({ length: length }, (_, index) => {
        return (
          <Stack key={index} spacing={2} direction={'row'}>
            <Skeleton sx={{ flex: 1 }} variant="rectangular" width={210} height={210} />
            <Box sx={{ width: '100%', flex: 1 }}>
              <Skeleton />
              <Skeleton />
              <Skeleton />
              <Skeleton />
              <Skeleton width="60%" />
            </Box>
          </Stack>
        )
      })}
    </Stack>
  )
}
